import type { ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import { typography } from '@/themes/typography';
import { color } from '@/themes/color';
import verifyEmailImage from '@/assets/hero-image.webp';
import logoIcon from '@/assets/Logos/logo.webp';

interface AuthPageLayoutProps {
  heading: string;
  description?: string;
  children: ReactNode;
  imageScale?: number;
  contentMaxWidth?: number;
  logoSpacing?: number;
}

export default function AuthPageLayout({
  heading,
  description,
  children,
  contentMaxWidth = 480,
  logoSpacing = 60,
}: AuthPageLayoutProps) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1.4fr 1fr' },
        backgroundColor: '#ffffff',
        minHeight: '100vh',
      }}
    >
      {/* Left side: Image and headline — hidden on mobile */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          position: 'relative',
          overflow: 'hidden',
          margin: '16px',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
        }}
      >
        <img
          src={verifyEmailImage}
          alt=""
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: '12px',
          }}
        />
        <h1
          style={{
            ...typography.heading1,
            fontSize: 'clamp(40px, 5vw, 72px)',
            color: color.terracotta.default,
            // textShadow: '0 2px 8px rgba(0,0,0,0.55), 0 1px 2px rgba(0,0,0,0.4)',
            position: 'relative',
            zIndex: 1,
            margin: 0,
            lineHeight: 1,
            textAlign: 'center',
            padding: '0 48px',
            maxWidth: '640px',
            marginTop: '32px',
          }}
        >
          The definitive guide to exploring UK vineyards
        </h1>
      </Box>

      {/* Right side: Form */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: { xs: '24px 16px', md: '20px' },
          backgroundColor: '#ffffff',
          overflow: 'auto',
        }}
      >
        <Box sx={{ width: '100%', maxWidth: contentMaxWidth, margin: 'auto' }}>
          {/* Branding */}
          <Box sx={{ mb: `${String(logoSpacing)}px`, textAlign: 'center' }}>
            <img
              src={logoIcon}
              alt="Vineyard Guide"
              style={{
                height: 80,
                width: 'auto',
                display: 'block',
                marginLeft: 'auto',
                marginRight: 'auto',
              }}
            />
          </Box>

          {/* Heading */}
          <Typography
            sx={{
              ...typography.heading2,
              fontWeight: 300,
              fontSize: { xs: '40px', md: '64px' },
              width: '100%',
              textAlign: 'center',
              lineHeight: 'normal',
              color: color.charcoal[500],
            }}
          >
            {heading}
          </Typography>

          {/* Description */}
          {description != null && description !== '' && (
            <Typography
              sx={{
                ...typography.bodyText,
                color: color.charcoal[400],
                textAlign: 'center',
                mt: '16px',
                mb: '32px',
              }}
            >
              {description}
            </Typography>
          )}

          {children}
        </Box>
      </Box>
    </Box>
  );
}
